import {
  Descendant,
  FormattedText,
  ImageElement,
  PopulatedNode,
  VariableElement,
} from '../../editor/types'
import { getInsuranceCoverageField } from '../../fields'
import { Field, FileDBValue, Template } from '../../types'
import { ShortcutArgs } from '../../types/admin/assessments'

import cloneDeep from 'lodash.clonedeep'
import { Text } from 'slate'

import { baseMidwifeFields } from '../../collections/fields/practice'
import { FieldTypes } from '../../constants/FieldTypes'
import { getAssessmentFieldFromPath } from '../../fields/assessment/field'
import { getPrimaryIsMedicaid } from '../assessments'
import { getDateString } from '../dates'
import { formatPhoneNumber, getFieldFromPath } from '../fields'
import { getShortcutValue } from './shortcuts'
import { getFormattedFieldValue } from './utils'

export function getTemplateValue(node: VariableElement, shortcutArgs: ShortcutArgs) {
  const { path, dataType } = node ?? {}
  const { user, assessment, plan, practice } = shortcutArgs
  if (!path) return undefined
  if (path === 'patient.fname' || path === 'fname') {
    return user?.fname
  }
  if (path === 'patient.lname' || path === 'lname') {
    return user?.lname
  }
  if (path === 'patient.dob' || path === 'dob') {
    return user?.dob ? getDateString(user.dob, 'short') : undefined
  }
  if (path === 'patient.phone' || path === 'phone') {
    return user?.phone ? formatPhoneNumber(user.phone) : undefined
  }
  if (node.shortcut) {
    return getShortcutValue(node.id, node.path, shortcutArgs)
  }
  if (node.path.startsWith('delivery.midwife')) {
    const subPath = node.path.split('.').slice(2).join('.')
    const field = getFieldFromPath(baseMidwifeFields, subPath) ?? {
      type: dataType,
      placeholder: '',
    }
    return getFormattedFieldValue(field as Field, subPath, practice ?? {})
  }
  if (
    node.path.startsWith('insuranceInfo.primaryCoverage') ||
    node.path.startsWith('insurance-info.primaryCoverage')
  ) {
    const subPath = node.path.split('.').slice(2).join('.')
    const field = getFieldFromPath(
      getInsuranceCoverageField(
        getPrimaryIsMedicaid(shortcutArgs.assessment?.mergedData?.insuranceInfo ?? {}) ?? false,
        true,
        true,
      ),
      subPath,
    ) ?? { type: dataType, placeholder: '' }
    const planValue = getFormattedFieldValue(field as Field, subPath, plan ?? {})
    const assessmentValue = getFormattedFieldValue(
      field as Field,
      subPath,
      assessment?.mergedData?.insuranceInfo?.primaryCoverage ?? {},
    )
    return planValue ?? assessmentValue
  }
  const field = getAssessmentFieldFromPath(true, assessment ?? null, node.path) ?? {
    type: dataType,
    placeholder: '',
  }
  return getFormattedFieldValue(field as Field, node.path, assessment?.mergedData)

  // return undefined
}

const populateFileVariableNode = (
  node: VariableElement,
  value: FileDBValue | undefined,
): {
  node: ImageElement | VariableElement
  errors: Array<any>
} => {
  const { imageOptions: options = {}, children } = node
  if (!value) {
    return {
      node,
      errors: [node],
    }
  }
  const { type, storagePath } = value ?? {}
  if (!type || !storagePath) {
    return {
      node,
      errors: [node],
    }
  }
  return {
    node: {
      type: 'image',
      value,
      children,
      options,
    },
    errors: [],
  }
}

export function populateNode(
  unpopulated: Descendant,
  shortcutArgs: ShortcutArgs,
): { node: any; errors: Array<VariableElement> } {
  const errorNodes: Array<VariableElement> = []
  if (unpopulated.type === 'variable') {
    const value = getTemplateValue(unpopulated, shortcutArgs)
    if (unpopulated.dataType && unpopulated.dataType === FieldTypes.FILE) {
      return populateFileVariableNode(unpopulated, value as FileDBValue)
    }
    const { text: _t, ...nodeArgs } = unpopulated.children?.[0] ?? {}
    let text = ''
    const isError = !value
    if (value) text = `${value}`
    else if (!unpopulated.optional) {
      text = `Error populating ${unpopulated.name}`
    }
    const node: FormattedText = {
      ...nodeArgs,
      text,
    }
    // if (unpopulated.isInline) node.isInline = true
    return { node, errors: isError ? [unpopulated] : [] }
  }
  if (!Text.isText(unpopulated) && unpopulated.children) {
    const populatedChildren = unpopulated.children?.map((n: any) => populateNode(n, shortcutArgs))
    const childErrors = populatedChildren.map((n: any) => n.errors).flat()
    errorNodes.push(...childErrors)
    return {
      node: {
        ...unpopulated,
        children: populatedChildren.map((n: any) => n.node),
      },
      errors: errorNodes,
    }
  }
  return { node: unpopulated, errors: errorNodes }
}

export function populateTemplate(
  template: Partial<Template>,
  shortcutArgs: ShortcutArgs,
): { populated: PopulatedNode[]; errors: VariableElement[] } {
  const { templateText } = cloneDeep(template)
  if (templateText) {
    return templateText.reduce(
      (acc, n) => {
        const { errors, node } = populateNode(n, shortcutArgs)
        acc.populated.push(node)
        acc.errors.push(...errors)
        return acc
      },
      { errors: [] as any[], populated: [] as any[] },
    )
  }
  return {
    populated: [
      {
        type: 'span',
        children: [
          {
            text: 'No template text - go to templates to add text to this template',
          },
        ],
      },
    ],
    errors: [],
  }
}
